import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { DuplicateCandidate, MergeChoice, MergePreview } from "@dentacore/shared-types";

import { duplicatePairs, mergePatients, mergePreview } from "../api/patients";
import { Modal } from "../ui/modal";
import styles from "./duplicates.module.css";

/** The words a merge preview uses for a detail. */
const DETAIL_NAMES: Record<string, string> = {
  firstName: "First name",
  lastName: "Last name",
  phoneE164: "Phone",
  email: "Email",
  address: "Address",
  sex: "Sex",
  insurerName: "Insurer",
  policyReference: "Policy reference",
  homeBranchId: "Home location",
  recallDueOn: "Recall due",
};

/** The words a merge preview uses for what moves. */
const TABLE_NAMES: Record<string, string> = {
  appointment: "appointments",
  treatmentPlan: "treatment plans",
  toothProcedure: "chart entries",
  clinicalNote: "notes",
  attachment: "images",
  invoice: "invoices",
  payment: "payments",
  messageThread: "WhatsApp threads",
  bookingRequest: "booking requests",
  waitlistEntry: "waitlist entries",
  patientFlag: "alerts",
  patientContact: "contacts",
  task: "tasks",
};

const shown = (value: unknown): string =>
  value === null || value === undefined || value === "" ? "—" : String(value);

/**
 * **Possible duplicates** — the administrator's list (PP-D14): every pair of
 * files with the same date of birth and a close name. *Review merge* opens the
 * preview; nothing moves until it is confirmed there.
 */
export function DuplicatesModal({ onClose }: { onClose: () => void }): React.JSX.Element {
  const [pairs, setPairs] = useState<{ a: DuplicateCandidate; b: DuplicateCandidate }[] | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [reviewing, setReviewing] = useState<{ keep: string; other: string } | null>(null);

  const load = (): void => {
    duplicatePairs()
      .then(setPairs)
      .catch((cause: unknown) =>
        setError(cause instanceof Error ? cause.message : "Could not read them."),
      );
  };
  useEffect(load, []);

  if (reviewing !== null) {
    return (
      <MergeModal
        keepId={reviewing.keep}
        otherId={reviewing.other}
        onClose={() => setReviewing(null)}
        onMerged={() => {
          setReviewing(null);
          load();
        }}
      />
    );
  }

  return (
    <Modal label="Possible duplicates" onClose={onClose} testId="duplicates-modal">
      <h2 className={styles.title}>Possible duplicates</h2>
      <p className={styles.sub}>Same date of birth, and a name a typo or two apart.</p>
      {error !== null && <p className={styles.error}>{error}</p>}
      {pairs === null ? (
        <p className={styles.sub}>Reading&hellip;</p>
      ) : pairs.length === 0 ? (
        <p className={styles.sub} data-testid="duplicates-empty">
          No files look like the same person.
        </p>
      ) : (
        <ul className={styles.pairs}>
          {pairs.map(({ a, b }) => (
            <li key={`${a.id}:${b.id}`} className={styles.pair} data-testid="duplicate-pair">
              <span>
                {a.name} <span className={styles.file}>#{a.fileNumber}</span>
              </span>
              <span>
                {b.name} <span className={styles.file}>#{b.fileNumber}</span>
              </span>
              <span className={styles.file}>born {shown(a.dateOfBirth)}</span>
              <button
                type="button"
                className={styles.secondary}
                onClick={() => setReviewing({ keep: a.id, other: b.id })}
                data-testid="duplicate-review"
              >
                Review merge
              </button>
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
}

/**
 * **The merge preview** (PP-D13): what moves, the details that differ picked
 * field by field (the kept file's by default), a tooth removed on only one file
 * settled here — then one confirm, which cannot be undone.
 */
export function MergeModal({
  keepId,
  otherId,
  onClose,
  onMerged,
}: {
  keepId: string;
  otherId: string;
  onClose: () => void;
  onMerged: (keptId: string) => void;
}): React.JSX.Element {
  const [ids, setIds] = useState({ keep: keepId, other: otherId });
  const [preview, setPreview] = useState<MergePreview | null>(null);
  const [details, setDetails] = useState<Record<string, "keep" | "other">>({});
  const [teeth, setTeeth] = useState<Record<string, "removed" | "present">>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setPreview(null);
    setDetails({});
    setTeeth({});
    mergePreview(ids.keep, ids.other)
      .then(setPreview)
      .catch((cause: unknown) =>
        setError(cause instanceof Error ? cause.message : "Could not preview."),
      );
  }, [ids]);

  const unsettled =
    preview?.removedOnOne.some((tooth) => teeth[String(tooth.fdiNumber)] === undefined) ?? true;

  const confirm = (): void => {
    setBusy(true);
    setError(null);
    const choice: MergeChoice = { details, teeth };
    mergePatients(ids.keep, ids.other, choice)
      .then((done) => onMerged(done.keptId))
      .catch((cause: unknown) =>
        setError(cause instanceof Error ? cause.message : "The merge did not happen."),
      )
      .finally(() => setBusy(false));
  };

  return (
    <Modal label="Merge two files" onClose={onClose} testId="merge-modal">
      <h2 className={styles.title}>Merge two files</h2>
      {error !== null && <p className={styles.error}>{error}</p>}
      {preview === null ? (
        <p className={styles.sub}>Reading&hellip;</p>
      ) : (
        <>
          <p className={styles.sub}>
            Keep <strong>{preview.keep.name}</strong> #{preview.keep.fileNumber}; move everything
            from <strong>{preview.other.name}</strong> #{preview.other.fileNumber} into it.{" "}
            <button
              type="button"
              className={styles.link}
              onClick={() => setIds({ keep: ids.other, other: ids.keep })}
              data-testid="merge-swap"
            >
              Keep the other one instead
            </button>
          </p>

          <p className={styles.moves} data-testid="merge-moves">
            Moves{" "}
            {Object.entries(preview.moves)
              .filter(([, count]) => count > 0)
              .map(([table, count]) => `${String(count)} ${TABLE_NAMES[table] ?? table}`)
              .join(", ") || "nothing else"}
            .
          </p>

          {preview.differing.length > 0 && (
            <table className={styles.details}>
              <thead>
                <tr>
                  <th>Detail</th>
                  <th>{preview.keep.name}</th>
                  <th>{preview.other.name}</th>
                </tr>
              </thead>
              <tbody>
                {preview.differing.map((row) => (
                  <tr key={row.field} data-testid="merge-detail">
                    <td>{DETAIL_NAMES[row.field] ?? row.field}</td>
                    {(["keep", "other"] as const).map((side) => (
                      <td key={side}>
                        <label className={styles.pick}>
                          <input
                            type="radio"
                            name={`detail-${row.field}`}
                            checked={(details[row.field] ?? "keep") === side}
                            onChange={() => setDetails((was) => ({ ...was, [row.field]: side }))}
                          />
                          {shown(row[side])}
                        </label>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {preview.removedOnOne.map((tooth) => (
            <fieldset key={tooth.fdiNumber} className={styles.tooth} data-testid="merge-tooth">
              <legend>
                Tooth {tooth.fdiNumber} is removed on{" "}
                {tooth.removedOn === "keep" ? preview.keep.name : preview.other.name}&rsquo;s file
                only
              </legend>
              {(["removed", "present"] as const).map((state) => (
                <label key={state} className={styles.pick}>
                  <input
                    type="radio"
                    name={`tooth-${String(tooth.fdiNumber)}`}
                    checked={teeth[String(tooth.fdiNumber)] === state}
                    onChange={() =>
                      setTeeth((was) => ({ ...was, [String(tooth.fdiNumber)]: state }))
                    }
                  />
                  {state === "removed" ? "It is removed" : "It is there"}
                </label>
              ))}
            </fieldset>
          ))}

          <p className={styles.warn}>
            This cannot be undone. {preview.other.name}&rsquo;s file is removed and marked merged.
          </p>
          <div className={styles.actions}>
            <button
              type="button"
              className={styles.primary}
              disabled={busy || unsettled}
              onClick={confirm}
              data-testid="merge-confirm"
            >
              Merge files
            </button>
            <button type="button" className={styles.secondary} onClick={onClose} disabled={busy}>
              Cancel
            </button>
          </div>
        </>
      )}
    </Modal>
  );
}

/**
 * **The warning a new or edited file gets** (PP-D14): files with the same date
 * of birth and a close name, each offered by link. Saving is never refused.
 */
export function DuplicateWarning({
  candidates,
}: {
  candidates: readonly DuplicateCandidate[];
}): React.JSX.Element | null {
  if (candidates.length === 0) return null;
  return (
    <div className={styles.warning} role="note" data-testid="duplicate-warning">
      <span className={styles.warningLabel}>Possibly already here</span>
      <span>
        {candidates.map((candidate, index) => (
          <span key={candidate.id}>
            {index > 0 && " · "}
            <Link to={`/patients/${candidate.id}`} className={styles.link}>
              {candidate.name} #{candidate.fileNumber}
            </Link>
          </span>
        ))}{" "}
        has the same date of birth. Open their file if it is the same person.
      </span>
    </div>
  );
}
